import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Images } from '../../../common/Assets/Images';
import './Styles.scss';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { museumApi } from '../../../services/MuseumServices';
import { ticketInitialValue, customerTicketValidation } from './TicketModel';
import countryList from 'react-select-country-list';
import { exhibitions, guides, ticketsApi, visitorTypes, getTourGuideByCid, getTourOperatorByLicense } from '../../../services/TicketsServices';
import { DispatchContext } from '../../../store';
import { SNACKBAR_OPEN } from '../../../reducers';
import SuccessPopup from '../../../layouts/Admin/Receptionist/SuccessPopup';
import TicketFilterTabs from './TicketFilterTabs';
import { useNavigate } from 'react-router';


export default function BookTicket() {
  const navigate = useNavigate();
  const dispatch = useContext(DispatchContext);
  const user = JSON.parse(localStorage.getItem('user'));
  const options = useMemo(() => {
    const data = countryList().getData();
    return [...data, { value: 'others', label: 'Others' }];
  }, []);
  const [museums, setMuseums] = useState([]);
  const [initialValue, setInitialValue] = useState(ticketInitialValue);
  const [show, setShow] = useState(false);
  const [museumId, setMuseumId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [visitorTypeOptions, setVisitorTypeOptions] = useState([]);
  const [guideOptions, setGuideOptions] = useState([]);
  const [exhibitionOptions, setExhibitionOptions] = useState([]);
  const [tourOperatorOptions, setTourOperatorOptions] = useState([]);
  const [guideCid, setGuideCid] = useState('');
  const [tourOperatorLicense, setTourOperatorLicense] = useState('');
  const [exNextPage, setExNextPage] = useState(1);
  const [exHasNext, setExHasNext] = useState(true);
  const [exLoading, setExLoading] = useState(false);
  
  const calculateDisplayAmount = (noOfVisitors, rate) => {
    const visitors = Number(noOfVisitors || 0);
    const visitorRate = Number(rate || 0);
    return visitors * visitorRate;
  };

  useEffect(() => {
    fetchMuseum();
  }, []);

  const fetchVisitorTypes = async () => {
    try {
      const res = await visitorTypes('get', null, { status: 'active' });
      setVisitorTypeOptions(res?.data || []);
    } catch (err) {
      console.error('Failed to load visitor types:', err);
      setVisitorTypeOptions([]);
    }
  };

  useEffect(() => {
    fetchVisitorTypes();
  }, []);

  const fetchExhibitions = async (page = 1) => {
    if (!museumId || exLoading) return;
    setExLoading(true);
    try {
      const res = await exhibitions('get', null, { page, per_page: 20, museum_id: museumId, status: 'active' });
      const raw = res?.data || [];
      const list = raw.some((x) => x?.museum_id)
        ? raw.filter((x) => String(x?.museum_id) === String(museumId))
        : raw;
      setExhibitionOptions((prev) => {
        const map = new Map(prev.map((x) => [String(x.id), x]));
        list.forEach((x) => map.set(String(x.id), x));
        return Array.from(map.values());
      });
      const next = res?.meta?.next_page;
      setExNextPage(next || null);
      setExHasNext(!!next);
    } catch (err) {
      console.error('Failed to load exhibitions:', err);
      if (page === 1) setExhibitionOptions([]);
      setExHasNext(false);
    } finally {
      setExLoading(false);
    }
  };

  useEffect(() => {
    if (!museumId) {
      setGuideOptions([]); setExhibitionOptions([]);
      setExNextPage(1); setExHasNext(true);
      return;
    }

    guides('get', null, { page: 1, per_page: 200, museum_id: museumId, status: 'active' })
      .then((res) => {
        const list = res?.data || [];
        const filtered = list.some((v) => String(v?.museum_id) === String(museumId))
          ? list.filter((v) => String(v?.museum_id) === String(museumId))
          : list;
        setGuideOptions(filtered);
      })
      .catch((err) => {
        console.error('Failed to load guides:', err);
        setGuideOptions([]);
      });
  }, [museumId]);

  const fetchMuseum = () => {
    museumApi('get').then(res => {
      setMuseums(res?.data);
    })
  }

  const handleGuideCidLookup = async (cid, setFieldValue) => {
    if (!cid || cid.length < 10) return;
    
    try {
      const response = await getTourGuideByCid(cid);
      if (response?.data?.users?.user?.length > 0) {
        const guide = response.data.users.user[0];
        setFieldValue('guide_name', guide.name || '');
        setFieldValue('guide_phone', guide.mobileNo || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Guide information found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Guide not found with this CID' }
      });
    }
  };

  const handleTourOperatorSearch = async (licenseNo, setFieldValue) => {
    if (!licenseNo) return;
    
    try {
      const response = await getTourOperatorByLicense(licenseNo);
      if (response?.data) {
        const operator = response.data;
        setFieldValue('guide_agent_name', operator.pro_Estb_Name || '');
        dispatch({
          type: SNACKBAR_OPEN,
          payload: { isNotify: true, severity: 'success', message: 'Tour operator found!' }
        });
      }
    } catch (error) {
      dispatch({
        type: SNACKBAR_OPEN,
        payload: { isNotify: true, severity: 'error', message: 'Tour operator not found with this license' }
      });
    }
  };

  const onSubmit = (values) => {
    const guideId = Number(values?.guide_id);
    const ticketValues = {
      museum_id: values.museum_id,
      visitor_type_id: Number(values.visitor_type_id),
      nationality: values.nationality || "",
      nationality_other: values.nationality_other || "",
      exhibition_ids: Array.isArray(values.exhibition_ids) ? values.exhibition_ids : [],
      no_of_visitor: parseInt(values.no_of_visitor),
      payment_due: Number(values.payment_due) || 0,
      payment_type: 'electronic',
      translations: {
        en: {
          remarks: '',
          refund_notes: '',
        }
      }
    }

    if (values.nationality === 'others') {
      ticketValues.nationality_other = values.nationality_other;
    }

    // Always include guide fields regardless of guide selection
    ticketValues.guide_name = values?.guide_name || '';
    ticketValues.guide_phone = values?.guide_phone || '';
    ticketValues.guide_agent_name = values?.guide_agent_name || '';

    // Only include guide_id if with guide and a guide is selected
    if (values?.withGuide === 'yes') {
      if (Number.isFinite(guideId) && guideId > 0) {
        ticketValues.guide_id = guideId;
      }
    }

    ticketsApi('post', { ...ticketValues }).then(res => {
      const ticketId = res?.data?.id;
      if (!ticketId) return;
      // Navigate to ticket page - payment will be started by ticket creator
      navigate(`/tickets/${ticketId}`);
    }).catch(err => {
      console.error('Ticket booking failed:', err);
      // Handle specific error for invalid ticket type
      if (err?.response?.data?.error === "Invalid or inactive ticket type for this museum.") {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {
            isNotify: true,
            severity: 'error',
            message: 'This ticket type is not available for the selected museum. Please select a different ticket type.'
          }
        });
      } else {
        // Handle other errors
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {
            isNotify: true,
            severity: 'error',
            message: err?.response?.data?.error || 'Failed to book ticket. Please try again.'
          }
        });
      }
    })
  }

  const handleClose = () => {
    setShow(false);
  }

  const handleForm = () => {
    setShowForm(!showForm);
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <img draggable={'false'} src={Images.ticketBanner}
          style={{ width: '100%', height: 100, objectFit: 'cover', filter: 'blur(2px)' }}
          alt="banner image" />
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: { xs: 8, sm: '30%' },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box>
            <Typography variant={'h6'} sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 16, sm: 20 } }}>
              <span className={'hand-cursor underline-hover'} onClick={() => navigate(-1)}>Home</span> / Your Tickets
            </Typography>
            <Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 15 } }}>Online Ticket Booking</Typography>
          </Box>
          <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {showForm ?
              <Button onClick={handleForm}
                fullWidth variant={'contained'} className={'text-capitalize'}>Book Ticket</Button> :
              <Button onClick={handleForm}
                fullWidth variant={'contained'} className={'text-capitalize'}>Your Tickets</Button>}
          </Box>
        </Box>
      </div>
      <Box sx={{ pt: 1, px: { xs: 2, sm: 5 } }}>
        {showForm &&
          <TicketFilterTabs />
        }
        {!showForm &&
          <Formik initialValues={initialValue}
            onSubmit={onSubmit}
            validationSchema={customerTicketValidation}
            validateOnBlur={true} enableReinitialize
          >
            {({ isSubmitting, touched, errors, values, setFieldValue, ...formik }) => (
              <Form>
                <Grid container>
                  <Grid item lg={4} md={4} sm={3} />
                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Card className={'box-shadow'} sx={{ p: { xs: 2, sm: 5 }, mt: 2 }}>
                      <>
                        <Typography sx={{ mb: 2, fontWeight: 700 }}>Book your ticket(s) now</Typography>
                        <Box sx={{ mb: 2 }}>
                          <Autocomplete
                            id="museums"
                            options={museums}
                            getOptionLabel={(option) => option?.name}
                            size={'small'}
                            onChange={(e, value) => {
                              console.log("Museum selected:", value);
                              console.log("Setting museumId to:", value?.id);
                              setFieldValue('museum_id', value?.id || null);
                              setMuseumId(value?.id);
                              setFieldValue('visitor_type_id', null);
                            }}
                            renderInput={(params) => <Field component={TextField} {...params}
                              label="Select Museum"
                              InputLabelProps={{
                                style: { color: '#929191', fontSize: 14 },
                              }}
                              name="museum_id" />}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Autocomplete
                            id="nationality"
                            options={options}
                            value={options.find(option => option.value === values.nationality) || null}
                            getOptionLabel={(option) => option?.label}
                            size={'small'}
                            onChange={(e, value) => {
                              setFieldValue('nationality', value?.value || null);
                            }}
                            renderInput={(params) => <Field component={TextField} {...params}
                              label="Select country"
                              InputLabelProps={{
                                style: { color: '#929191', fontSize: 14 },
                              }}
                              name="nationality" />}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          {values?.nationality === 'others' &&
                            <Field component={TextField} variant={'outlined'} size={'small'} fullWidth
                              label={'Specify nationality'} name="nationality_other"
                              InputLabelProps={{
                                style: { fontSize: 14, color: '#929191' }
                              }} />
                          }
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Autocomplete
                            id="visitor_type"
                            options={visitorTypeOptions}
                            value={visitorTypeOptions.find(opt => String(opt.id) === String(values.visitor_type_id)) || null}
                            getOptionLabel={(option) => option?.name || ''}
                            size={'small'}
                            onChange={(e, value) => {
                              setFieldValue('visitor_type_id', value?.id || null);
                              const fee = Number(value?.fee || 0);
                              const noOfVisitors = Number(values.no_of_visitor || 0);
                              setFieldValue('payment_due', fee * noOfVisitors);
                            }}
                            renderInput={(params) => <Field component={TextField} {...params}
                              label="Select visitor type"
                              InputLabelProps={{
                                style: { color: '#929191', fontSize: 14 },
                              }}
                              name="visitor_type_id" />}
                          />
                        </Box>
                                                <Box sx={{ mb: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Guide</InputLabel>
                            <Select
                              label="Guide"
                              value={values?.withGuide || 'no'}
                              onChange={(e) => {
                                const next = e.target.value;
                                setFieldValue('withGuide', next);
                                if (next !== 'yes') {
                                  setFieldValue('guide_id', null);
                                  setFieldValue('guide_name', '');
                                  setFieldValue('guide_phone', '');
                                  setFieldValue('guide_agent_name', '');
                                }
                              }}
                            >
                              <MenuItem value="no">Without Guide</MenuItem>
                              <MenuItem value="yes">With Guide</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {values?.withGuide === 'yes' && (
                          <Box sx={{ mb: 2 }}>
                            <Field
                              component={TextField}
                              variant={'outlined'}
                              size={'small'}
                              fullWidth
                              label="Guide CID"
                              name="guide_cid"
                              value={guideCid}
                              onChange={(e) => {
                                const value = e.target.value;
                                setGuideCid(value);
                                
                                // Automatically lookup when 11 digits are entered
                                if (value.length === 11) {
                                  console.log('BookTicket: Auto-triggering CID lookup for 11 digits:', value);
                                  handleGuideCidLookup(value, setFieldValue);
                                }
                              }}
                              InputLabelProps={{
                                style: { fontSize: 14, color: '#929191' },
                              }}
                            />
                          </Box>
                        )}

                        {values?.withGuide === 'yes' ? (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Guide name"
                                name="guide_name"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Guide phone"
                                name="guide_phone"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Guide Agent Name"
                                name="guide_agent_name"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Name"
                                name="guide_name"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Contact"
                                name="guide_phone"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Field
                                component={TextField}
                                variant={'outlined'}
                                size={'small'}
                                fullWidth
                                label="Place"
                                name="guide_agent_name"
                                InputLabelProps={{
                                  style: { fontSize: 14, color: '#929191' },
                                }}
                              />
                            </Box>
                          </>
                        )}
                        <Box sx={{ mb: 2 }}>
                          <Autocomplete
                            multiple
                            id="exhibitions"
                            options={exhibitionOptions}
                            value={exhibitionOptions.filter((v) => (values.exhibition_ids || []).includes(v.id))}
                            getOptionLabel={(option) => option?.name || option?.translations?.en?.name || ''}
                            isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                            size={'small'}
                            onOpen={() => { if (exhibitionOptions.length === 0) fetchExhibitions(1); }}
                            ListboxProps={{
                              onScroll: (e) => {
                                const list = e.currentTarget;
                                if (list.scrollTop + list.clientHeight >= list.scrollHeight - 1) {
                                  if (exHasNext && exNextPage) fetchExhibitions(exNextPage);
                                }
                              },
                            }}
                            onChange={(e, next) => {
                              setFieldValue(
                                'exhibition_ids',
                                (next || []).map((x) => x.id)
                              );
                            }}
                            renderInput={(params) => (
                              <Field
                                component={TextField}
                                {...params}
                                label="Select exhibitions"
                                InputLabelProps={{
                                  style: { color: '#929191', fontSize: 14 },
                                }}
                                name="exhibition_ids"
                              />
                            )}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Field component={TextField} variant={'outlined'} size={'small'} fullWidth
                            label={'No of visitor'} name="no_of_visitor" type={'number'}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue('no_of_visitor', value);
                              const selectedVisitorType = visitorTypeOptions.find(vt =>
                                String(vt.id) === String(values.visitor_type_id)
                              );
                              const fee = Number(selectedVisitorType?.fee || 0);
                              setFieldValue('payment_due', calculateDisplayAmount(value, fee));
                            }}
                            InputLabelProps={{
                              style: { fontSize: 14, color: '#929191' },
                              shrink: true,
                            }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Field component={TextField} variant={'outlined'} size={'small'} fullWidth
                            label={'Ticket amount'} name="payment_due" type={'number'} disabled
                            InputLabelProps={{
                              style: { fontSize: 14, color: '#929191' }
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  Nu.
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Payment Type</InputLabel>
                            <Select
                              label="Payment Type"
                              value={"electronic"}
                              onChange={(e) => {
                                const next = e.target.value;
                                setFieldValue("payment_type", next);
                                if (next !== "electronic") {
                                  setFieldValue("transaction_no", "");
                                }
                              }}
                            >
                              <MenuItem value="electronic">Electronic</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {/*<Box sx={{mb: 2}}>*/}
                        {/*  <Field component={TextField} variant={'outlined'} size={'small'} fullWidth label={'Validity'}*/}
                        {/*         name="validity" type={'date'}*/}
                        {/*         InputLabelProps={{*/}
                        {/*           style: {fontSize: 14, color: '#929191'},*/}
                        {/*           shrink: true*/}
                        {/*         }}/>*/}
                        {/*</Box>*/}
                      </>
                      <Box>
                        <Button fullWidth variant={'contained'} type={'submit'}
                          className={'text-capitalize'}>Proceed</Button>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        }
        {show &&
          <SuccessPopup show={show} handleClose={handleClose} ticketId={3} />
        }
      </Box>
    </>
  );
}